/**
 * What the back key does at Kiddora's own roots — the only part left to decide, with no DOM in
 * sight so it can be tested under Node without a browser.
 *
 * Everywhere else, back is plain: whatever real history says is genuinely where the visitor came
 * from, exactly like any other web page, and there is nothing here to second-guess.
 *
 * Three roots: `#/play` (a child's shelf), `#/class` (a teacher's roster), `#/` (signed out).
 * "Root" only matters in an installed app or the Android shell — there, a back press at one of
 * these has to close Kiddora rather than leaving the site behind (nothing does that in a browser
 * tab) or doing nothing. In a browser tab a root is left alone: there is a real previous page, and
 * taking that away is the behaviour people rightly hate.
 */

import type { Route } from "./route.js";

const ROOT_KINDS: ReadonlySet<Route["kind"]> = new Set(["play", "class", "landing"]);

export function isRootScreen(route: Route): boolean {
  return ROOT_KINDS.has(route.kind);
}

/**
 * A sentinel history entry, pushed while sitting on a root screen.
 *
 * Without it, back at a root leaves the site entirely — the browser pops to whatever preceded us,
 * which in an installed app is nothing, so the app disappears with no chance to intervene. With
 * one spare entry underneath, the press produces a `popstate` we can answer.
 *
 * Only in a standalone app. In a browser tab the previous page is a real destination and trapping
 * back there is the behaviour people rightly hate.
 */
export const EXIT_TRAP = { kiddoraExitTrap: true } as const;

export const isExitTrap = (state: unknown): boolean =>
  Boolean(state && typeof state === "object" && "kiddoraExitTrap" in state);

/**
 * Whether a `popstate` means the parent pressed back on a root screen.
 *
 * **This is here, pure and tested, because the obvious formulation of it is wrong.** The trap is
 * pushed while standing on the root, so the app is sitting ON the trap entry. Pressing back pops
 * it and lands on the entry BENEATH — whose state is not the trap. So "the trap was just consumed"
 * reads as `history.state` NOT being a trap, which is the opposite of how it first looks.
 *
 * Getting it backwards is silent: the shell keeps working, because Android never calls the
 * WebView's back and instead dispatches an event the other handler answers. Only an installed PWA
 * takes this path, and there the symptom is a back press that appears to do nothing.
 *
 * @param stateAfterPop `history.state` read AFTER the pop, not before.
 */
export function popIsExitPress(input: {
  route: Route;
  /** The shell, or an installed PWA. In a browser tab back is the browser's to handle. */
  standalone: boolean;
  stateAfterPop: unknown;
}): boolean {
  if (!input.standalone) return false;
  // Not on a root: the browser has already gone where its history said, and that is genuinely
  // where the visitor came from. There is nothing left for us to do.
  if (!isRootScreen(input.route)) return false;
  // Landed on another trap. Traps accumulate when a root is reached more than once, and popping
  // one of those is not the press that should close the app.
  return !isExitTrap(input.stateAfterPop);
}
